import axios from 'axios';
import axiosRetry, { isNetworkOrIdempotentRequestError } from 'axios-retry';

// LOCALIZATION
import { getCurrentLang } from '@/locale/i18n';

// CONSTANTS
import { AUTH_REFRESH_TOKEN } from '@/constants/api';

// UTILITY
import LocalStore from '@/utility/store';
import { getTimezone } from '@/utility/date';

// STORE
import { logout } from '@/store/actions/auth';
import store from '@/store/configureStore';

// set API configs
axios.defaults.baseURL = import.meta.env.VITE_BASE_API;

export const initAxiosRetry = () => {
  axiosRetry(axios, {
    retries: 1,
    retryDelay: () => 5000,
    shouldResetTimeout: true,
    retryCondition: error => {
      return isNetworkOrIdempotentRequestError(error) && error.response?.status !== 519;
    },
  });
};

export const deleteAuthorizationToken = () => {
  delete axios.defaults.headers.Authorization;
};

export const updateHeadersLang = lng => {
  axios.defaults.headers['X-localization'] = lng;
};

export const setAuthorizationToken = (token, tokenType) => {
  const lang = getCurrentLang();
  const timezone = getTimezone();

  if (token) {
    axios.defaults.headers = {
      Authorization: `${tokenType} ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'x-timezone': timezone,
      'X-localization': lang,
    };
  } else {
    deleteAuthorizationToken();
  }
};

export const createAxiosResponseInterceptor = () => {
  const interceptor = axios.interceptors.response.use(
    response => response,
    error => {
      const errorResponse = error?.response || {};

      // Reject promise if usual error
      if (errorResponse?.status !== 401) {
        // if (errorResponse?.status === 519) {
        //   store.dispatch({ type: setMaintenanceMode.types.INIT, payload: true });
        //   history.push(ROUTE_ROOT);
        // }

        return Promise.reject(error);
      }

      /*
       * When response code is 401, try to refresh the token.
       * Eject the interceptor so it doesn't loop in case
       * token refresh causes the 401 response
       */
      axios.interceptors.response.eject(interceptor);

      return axios
        .get(AUTH_REFRESH_TOKEN)
        .then(response => {
          error.config.headers.Authorization = `${response.data.token_type} ${response.data.token}`;
          setAuthorizationToken(response.data.token, response.data.token_type);
          LocalStore.set('token', response.data.token);

          return axios(error.response.config);
        })
        .catch(err => {
          store.dispatch({ type: logout.types.INIT });
          return Promise.reject(err);
        })
        .finally(createAxiosResponseInterceptor);
    },
  );
};

export const axiosWP = axios.create({
  baseURL: import.meta.env.VITE_WP_API,
});

/** Consumer / rewards API (api.mybundle.tv) — not the partner reporting API (VITE_BASE_API). */
export const axiosMyBundle = axios.create({
  baseURL: import.meta.env.VITE_MYBUNDLE_API,
  headers: {
    Accept: 'application/json',
  },
});

axiosMyBundle.interceptors.request.use(config => {
  const { headers: defaultHeaders } = axios.defaults;

  // Consumer API (api.mybundle.tv) is public for reward pages; partner dashboard JWTs are rejected.
  const headers = {
    ...config.headers,
    Accept: 'application/json',
    ...(defaultHeaders?.['X-localization'] && {
      'X-localization': defaultHeaders['X-localization'],
    }),
  };

  delete headers.Authorization;

  return {
    ...config,
    headers,
  };
});
